// import node module libraries
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useMutation, useLazyQuery } from "@apollo/client/react";

import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import FormProvider from "components/general/hook-form/FormProvider";

import { Col, Row, Card, Form, Button, Image, Alert, InputGroup } from "react-bootstrap";

// import media files
import Logo from "assets/images/brand/logo/logo.svg";
import useAuth from "hooks/useAuth";
import { PATH_PAGE } from "routes/Paths";
import { getUserGUID } from "utils/jwt";
import { LOGIN_USER } from "helper/graphql/mutations";
import { GET_USER_DETAILS } from "helper/graphql/queries";

const SignIn = () => {
  const navigate = useNavigate();

  const { login } = useAuth();

  const [loginUser, { loading: loginLoad }] = useMutation(LOGIN_USER);
  const [getUserInfo, { loading: userLoad }] = useLazyQuery(GET_USER_DETAILS);

  const [showPass, setShowPass] = useState(false);

  const LoginSchema = Yup.object().shape({
    username: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    username: "",
    password: "",
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    setError,
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const onLogin = (payload) => {
    loginUser({
      variables: payload,
      onCompleted: (data) => {
        const _token = data.loginUser.token;
        login(_token)

        localStorage.setItem("isAuthenticated", true);
        localStorage.setItem("accessToken", _token);

        getUserInfo({
          variables: { guid: getUserGUID(_token) },
          onCompleted: (data) => {
            localStorage.setItem("user", JSON.stringify(data?.allUsers?.edges[0]?.node || ''));
            navigate(PATH_PAGE.root);
          },
        });
      },
      onError: (err) => {
        setError("afterSubmit", { ...err, message: err.message });
      },
    });
  };

  const showPassHandler = () => setShowPass(prev => !prev)

  const isLoading = loginLoad || userLoad;

  return (
    <Fragment>
      <div className="auth-wrapper">
        <Row className="align-items-center justify-content-center g-0 min-vh-100">
          <Col xxl={4} xl={5} lg={6} md={8} sm={10} className="py-8 py-xl-0">
            <Card className="auth-card">
              <Card.Body className="p-5 p-lg-6">
                {/* Logo & Header */}
                <div className="auth-header mb-5">
                  <Link to="/" className="logo-link">
                    <Image src={Logo} className="auth-logo" alt="Logo" />
                  </Link>
                  <h1 className="auth-title">Welcome Back</h1>
                  <p className="auth-subtitle">
                    Sign in to continue to your account
                  </p>
                </div>

                {/* Error Alert */}
                {!!errors.afterSubmit && (
                  <Alert variant="danger" className="modern-alert">
                    <i className="fe fe-alert-circle me-2"></i>
                    {errors.afterSubmit.message}
                  </Alert>
                )}

                {/* Form */}
                <FormProvider methods={methods} onSubmit={handleSubmit(onLogin)}>
                  <Row>
                    <Col xs={12} className="mb-4">
                      <Form.Label className="modern-label">
                        Email Address
                      </Form.Label>
                      <div className="input-icon-wrapper">
                        <i className="fe fe-mail input-icon"></i>
                        <Form.Control
                          type="email"
                          id="email"
                          {...register("username")}
                          placeholder="Enter your email"
                          className="modern-input"
                          required
                        />
                      </div>
                    </Col>

                    <Col xs={12} className="mb-4">
                      <Form.Label className="modern-label">
                        Password
                      </Form.Label>
                      <InputGroup className="modern-input-group">
                        <div className="input-icon-wrapper flex-grow-1">
                          <i className="fe fe-lock input-icon"></i>
                          <Form.Control
                            type={showPass ? "text" : "password"}
                            {...register("password")}
                            id="password"
                            placeholder="Enter your password"
                            className="modern-input with-toggle"
                            required
                          />
                        </div>
                        <Button 
                          variant=""
                          className="password-toggle"
                          onClick={showPassHandler}
                          type="button"
                        >
                          <i className={showPass ? 'fe fe-eye-off' : 'fe fe-eye'}></i>
                        </Button>
                      </InputGroup>
                    </Col>

                    <Col xs={12} className="mb-4">
                      <div className="d-flex justify-content-between align-items-center">
                        <Form.Check 
                          type="checkbox" 
                          id="remember-me"
                          label="Remember me"
                          className="modern-checkbox"
                        />
                        <Link to="/authentication/forget-password" className="forgot-link">
                          Forgot password?
                        </Link>
                      </div>
                    </Col>

                    <Col xs={12} className="mb-4">
                      <Button
                        variant=""
                        type="submit"
                        disabled={isLoading}
                        className="auth-submit-btn w-100"
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Signing in...
                          </>
                        ) : (
                          <>
                            <i className="fe fe-log-in me-2"></i>
                            Sign In
                          </>
                        )}
                      </Button>
                    </Col>

                    <Col xs={12}>
                      <p className="auth-footer-text">
                        Don't have an account?{" "}
                        <Link to="/authentication/sign-up" className="auth-link">
                          Sign up for free
                        </Link>
                      </p>
                    </Col>
                  </Row>
                </FormProvider>
              </Card.Body>
            </Card>

            {/* Footer */}
            <div className="auth-page-footer">
              <p className="mb-0">
                © {new Date().getFullYear()} Hope Arthritis Foundation. All rights reserved.
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <style jsx global>{`
        /* Auth Wrapper */
        .auth-wrapper {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          min-height: 100vh;
          position: relative;
        }

        .auth-wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23F68521' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.5;
          pointer-events: none;
        }

        /* Auth Card */
        .auth-card {
          border: none;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          position: relative;
          overflow: hidden;
        }

        .auth-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #F68521 0%, #1d9eb0 100%);
        }

        /* Auth Header */
        .auth-header {
          text-align: center;
        }

        .logo-link {
          display: inline-block;
          margin-bottom: 2rem;
        }

        .auth-logo {
          height: 48px;
          transition: transform 0.3s ease;
        }

        .logo-link:hover .auth-logo {
          transform: scale(1.05);
        }

        .auth-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .auth-subtitle {
          font-size: 1rem;
          color: #64748b;
          margin: 0;
        }

        /* Modern Alert */
        .modern-alert {
          border: none;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 2rem;
          display: flex;
          align-items: center;
        }

        /* Form Elements */
        .modern-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #475569;
          margin-bottom: 0.5rem;
        }

        .input-icon-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 1.125rem;
          z-index: 5;
        }

        .modern-input {
          height: 52px;
          padding-left: 3rem;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .modern-input:focus {
          border-color: #F68521;
          box-shadow: 0 0 0 4px rgba(246, 133, 33, 0.1);
        }

        .modern-input::placeholder {
          color: #cbd5e1;
        }

        /* Input Group for Password */
        .modern-input-group {
          display: flex;
          gap: 0.5rem;
        }

        .modern-input.with-toggle {
          border-top-right-radius: 12px;
          border-bottom-right-radius: 12px;
        }

        .password-toggle {
          min-width: 52px;
          height: 52px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: white;
          color: #64748b;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .password-toggle:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #F68521;
        }

        .password-toggle i {
          font-size: 1.125rem;
        }

        /* Checkbox */
        .modern-checkbox {
          font-size: 0.875rem;
          color: #64748b;
        }

        .modern-checkbox .form-check-input {
          width: 1.125rem;
          height: 1.125rem;
          border: 2px solid #cbd5e1;
          border-radius: 6px;
        }

        .modern-checkbox .form-check-input:checked {
          background-color: #F68521;
          border-color: #F68521;
        }

        /* Links */
        .forgot-link {
          font-size: 0.875rem;
          font-weight: 600;
          color: #F68521;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .forgot-link:hover {
          color: #1d9eb0;
        }

        /* Submit Button */
        .auth-submit-btn {
          height: 52px;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 700;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(246, 133, 33, 0.3);
        }

        .auth-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(246, 133, 33, 0.4);
          color: white;
        }

        .auth-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        /* Footer Text */
        .auth-footer-text {
          text-align: center;
          font-size: 0.9375rem;
          color: #64748b;
          margin: 0;
        }

        .auth-link {
          color: #F68521;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .auth-link:hover {
          color: #1d9eb0;
        }

        /* Page Footer */
        .auth-page-footer {
          text-align: center;
          padding: 2rem 0;
          color: #94a3b8;
          font-size: 0.875rem;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .auth-title {
            font-size: 1.75rem;
          }

          .auth-card {
            border-radius: 16px;
          }

          .auth-logo {
            height: 40px;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default SignIn;